import React, { useRef, useState, useMemo } from 'react';
import { nodeCardHeight, NODE_CARD_WIDTH } from './nodeCardMetrics';

/**
 * LoopLinkLayer — drag a link from one card to another.
 *
 * This is the manual move a systems modeller makes when they close a loop:
 * "…and what does this eventually feed back into?", then an arrow drawn back to
 * something that came earlier. It stays a human act on purpose — the map only
 * carries relations somebody chose (specs/impact-path-v2-prd.md §2), and a loop
 * is the strongest claim on the map, so it is the last thing that should appear
 * by itself.
 *
 * A handle sits on the top edge of every card, in that card's own colour, and
 * reads as the knot's sibling: a small solid dot that means "a relation starts
 * here". Drag it and a dashed line follows the cursor; the card under the
 * cursor rings to show what would be linked; let go and the effect-type choice
 * opens, because a loop-back link needs a polarity like any other edge — that
 * polarity is what decides whether the loop reinforces or balances.
 *
 * The link is written to pathway_connections, which the layout treats as an
 * overlay: it never repositions anything (the anti-reflow rule), which is
 * exactly what a back-edge needs, since a tree cannot lay out a cycle.
 */

const HANDLE_RADIUS = 7;
const HIT_RADIUS = 18;

export default function LoopLinkLayer({
  nodes = [],
  positions = {},
  // Blocker cards are objects on the map like any other, so they carry the same
  // handle: [{ id, x, y, width, height }] in SVG coordinates.
  blockerBoxes = [],
  enabled = false,
  onLink,
  cardWidth = NODE_CARD_WIDTH,
}) {
  const gRef = useRef(null);
  // { fromId, x, y, targetId } — x/y in SVG coordinates.
  const [drag, setDrag] = useState(null);

  // Every box on the map — node card or blocker card — with the geometry the
  // handle needs. A card carrying scores is taller than the base 90, so asking
  // the renderer's own height function is the only way the handle stays on the
  // top edge instead of sinking into the card.
  const boxes = useMemo(() => {
    if (!enabled) return [];
    const fromNodes = nodes
      .map((n) => {
        const pos = positions[n.id];
        if (!pos) return null;
        const height = nodeCardHeight(n);
        return {
          id: n.id,
          kind: 'node',
          x: pos.x,
          y: pos.y,
          width: cardWidth,
          height,
          color: n.borderColor || '#7d8fa3',
        };
      })
      .filter(Boolean);
    const fromBlockers = blockerBoxes.map((b) => ({
      id: b.id,
      kind: 'blocker',
      x: b.x,
      y: b.y,
      width: b.width,
      height: b.height,
      color: '#EF5350',
    }));
    return [...fromNodes, ...fromBlockers];
  }, [nodes, positions, blockerBoxes, enabled, cardWidth]);

  if (!enabled) return null;

  const toSvg = (clientX, clientY) => {
    const ctm = gRef.current?.getScreenCTM();
    const svg = gRef.current?.ownerSVGElement;
    if (!ctm || !svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(ctm.inverse());
  };

  // Which box is under the cursor. A little slack vertically so a near-miss
  // still lands on the card the cursor is clearly over.
  const boxAt = (p, exceptId) => boxes.find((b) => (
    b.id !== exceptId
    && Math.abs(p.x - b.x) <= b.width / 2
    && Math.abs(p.y - b.y) <= b.height / 2 + 14
  )) || null;

  const start = (e, fromId, fromKind) => {
    // The canvas pans on mousedown; a drag that starts on a handle is not a pan.
    e.stopPropagation();
    e.preventDefault();
    const p = toSvg(e.clientX, e.clientY);
    if (!p) return;
    // Capture keeps the drag alive when the cursor leaves the handle. Not
    // fatal if the browser refuses the pointer id — the drag still tracks via
    // the layer's own move handler.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    setDrag({ fromId, fromKind, x: p.x, y: p.y, targetId: null, targetKind: null });
  };

  const move = (e) => {
    if (!drag) return;
    const p = toSvg(e.clientX, e.clientY);
    if (!p) return;
    setDrag((d) => {
      if (!d) return d;
      const target = boxAt(p, d.fromId);
      return { ...d, x: p.x, y: p.y, targetId: target?.id || null, targetKind: target?.kind || null };
    });
  };

  const end = (e) => {
    if (!drag) return;
    e.stopPropagation();
    const { fromId, fromKind, targetId, targetKind } = drag;
    setDrag(null);
    if (!targetId) return;
    onLink?.(
      { id: fromId, kind: fromKind },
      { id: targetId, kind: targetKind },
      { x: e.clientX, y: e.clientY },
    );
  };

  const from = drag ? boxes.find((b) => b.id === drag.fromId) : null;
  const target = drag?.targetId ? boxes.find((b) => b.id === drag.targetId) : null;

  return (
    <g
      ref={gRef}
      className="loop-link-layer"
      onPointerMove={drag ? move : undefined}
      onPointerUp={drag ? end : undefined}
    >
      {/* While dragging, the whole canvas is the drop area — without this the
          pointer only reports while it is over a handle or a card. */}
      {drag && (
        <rect
          x={-100000}
          y={-100000}
          width={200000}
          height={200000}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
        />
      )}
      {/* The link being drawn. Dashed while it is still a proposal. */}
      {drag && from && (
        <g style={{ pointerEvents: 'none' }}>
          <path
            d={`M ${from.x} ${from.y - from.height / 2} C ${from.x} ${from.y - from.height / 2 - 120}, ${drag.x} ${drag.y - 120}, ${drag.x} ${drag.y}`}
            fill="none"
            stroke="#D4A72C"
            strokeWidth={2.5}
            strokeDasharray="7 6"
            strokeLinecap="round"
          />
          <circle cx={drag.x} cy={drag.y} r={5} fill="#D4A72C" />
        </g>
      )}

      {/* What the link would land on. */}
      {target && (
        <rect
          x={target.x - target.width / 2 - 6}
          y={target.y - target.height / 2 - 6}
          width={target.width + 12}
          height={target.height + 12}
          rx={14}
          fill="rgba(212, 167, 44, 0.1)"
          stroke="#D4A72C"
          strokeWidth={2.5}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {boxes.map((h) => (
        <g
          key={h.id}
          className={`loop-link-handle loop-link-handle--${h.kind}`}
          transform={`translate(${h.x}, ${h.y - h.height / 2})`}
          style={{ cursor: drag ? 'crosshair' : 'grab' }}
          onPointerDown={(e) => start(e, h.id, h.kind)}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={() => setDrag(null)}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <circle r={HIT_RADIUS} fill="transparent" />
          <circle
            r={drag?.fromId === h.id ? HANDLE_RADIUS + 2 : HANDLE_RADIUS}
            fill={h.color}
            stroke="#0a0a0a"
            strokeWidth={1.5}
          />
        </g>
      ))}
    </g>
  );
}
